/**
 * Referral Service
 * API calls for referral-related operations
 */

import { apiClient as api } from './apiClient';

// ============================================
// INTERFACES
// ============================================

export interface PendingReward {
    type: 'signup' | 'subscribe';
    amount: number;
    referralId: string;
    referrerUsername?: string;
}

export interface ClaimRewardResult {
    type: 'signup' | 'subscribe';
    amount: number;
    newBalance: number;
}

export interface ReferralStats {
    referralCode: string;
    totalReferrals: number;
    signupRewards: number;
    subscribeRewards: number;
    totalEarned: number;
    pendingRewards: number;
    recentReferrals: Array<{
        username: string;
        status: string;
        createdAt: string;
    }>;
}

export interface PendingRewardsResponse {
    hasPendingRewards: boolean;
    pendingRewards: PendingReward[];
    totalPending: number;
}

// ============================================
// API FUNCTIONS
// ============================================

/**
 * Get user's referral code
 */
export async function getReferralCode(): Promise<string> {
    const response = await api.get<{ success: boolean; data: { referralCode: string } }>('/referrals/code');
    return response.data.data.referralCode;
}

/**
 * Get full referral link
 */
export async function getReferralLink(): Promise<{ referralCode: string; referralLink: string }> {
    const response = await api.get<{
        success: boolean;
        data: { referralCode: string; referralLink: string };
    }>('/referrals/link');
    return response.data.data;
}

/**
 * Get referral statistics
 */
export async function getReferralStats(): Promise<ReferralStats> {
    const response = await api.get<{ success: boolean; data: ReferralStats }>('/referrals/stats');
    return response.data.data;
}

/**
 * Get pending rewards (for showing animation on page load)
 */
export async function getPendingRewards(): Promise<PendingRewardsResponse> {
    const response = await api.get<{ success: boolean; data: PendingRewardsResponse }>('/referrals/pending-rewards');
    return response.data.data;
}

/**
 * Claim a specific reward
 */
export async function claimReward(
    referralId: string,
    rewardType: 'signup' | 'subscribe'
): Promise<ClaimRewardResult> {
    const response = await api.post<{ success: boolean; data: ClaimRewardResult }>('/referrals/claim', {
        referralId,
        rewardType,
    });
    return response.data.data;
}

/**
 * Claim all pending rewards at once
 */
export async function claimAllRewards(): Promise<{
    claimed: ClaimRewardResult[];
    totalClaimed: number;
    newBalance: number | null;
}> {
    const response = await api.post<{
        success: boolean;
        data: {
            claimed: ClaimRewardResult[];
            totalClaimed: number;
            newBalance: number | null;
        };
    }>('/referrals/claim-all', {});
    return response.data.data;
}

/**
 * Validate a referral code (for signup form)
 */
export async function validateReferralCode(code: string): Promise<{
    valid: boolean;
    referrerUsername?: string;
}> {
    try {
        const response = await api.get<{
            success: boolean;
            data: { valid: boolean; referrerUsername?: string };
        }>(`/referrals/validate/${code}`);
        return response.data.data;
    } catch {
        return { valid: false };
    }
}

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Copy referral link to clipboard
 */
export async function copyReferralLinkToClipboard(link: string): Promise<boolean> {
    try {
        await navigator.clipboard.writeText(link);
        return true;
    } catch {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = link;
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        try {
            document.execCommand('copy');
            document.body.removeChild(textArea);
            return true;
        } catch {
            document.body.removeChild(textArea);
            return false;
        }
    }
}

/**
 * Format referral status for display
 */
export function formatReferralStatus(status: string, lang = 'zh-TW'): string {
    const statusNames: Record<string, Record<string, string>> = {
        pending: { 'zh-TW': '等待驗證', en: 'Pending' },
        signup_rewarded: { 'zh-TW': '已註冊', en: 'Signed Up' },
        subscribed: { 'zh-TW': '已訂閱', en: 'Subscribed' },
        subscribe_rewarded: { 'zh-TW': '已訂閱獎勵', en: 'Subscribe Rewarded' },
    };
    return statusNames[status]?.[lang] || statusNames[status]?.en || status;
}

/**
 * Get reward type display name
 */
export function getRewardTypeName(type: 'signup' | 'subscribe', lang = 'zh-TW'): string {
    const names: Record<string, Record<string, string>> = {
        signup: { 'zh-TW': '好友註冊獎勵', en: 'Referral Signup Reward' },
        subscribe: { 'zh-TW': '好友訂閱獎勵', en: 'Referral Subscribe Reward' },
    };
    return names[type]?.[lang] || names[type]?.en || type;
}
